import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useProfile } from "src/shared/hooks/useProfile"
import useEscapeKey from "src/shared/hooks/useEscapeKey"
import { useHeader } from "./hooks/useHeader"
import { useModulos } from "src/shared/hooks/useModulos"
import { removeItems } from "../detalle-compra/DetalleCompra.helpers"
import { getSaveItems } from "../productos/Products.helpers"
import { TableSkeleton } from "src/shared/components/layout/skeleton/TableSkeleton/TableSkeleton"
import FlexibleTable, { FlexibleTableRef, TableFilter } from "src/shared/components/data-display/FlexibleTable/FlexibleTable"
import TabMenu from "src/shared/components/navigation/tab-menu/TabMenu"
import Screen from "src/shared/components/layout/screen/Screen"
import { DrawerSection } from "./drawer/Drawer"
import { toggleDrawer } from "src/store/navigation/navigationSlice"
import { InputText } from "src/shared/components/forms"
import { RootState } from "src/store/store"
import { selectOrder } from "src/store/orders/ordersSlice"
import { EstadosOrdenHistorial, OrdenesQuery, useOrdenesPaginadasLazyQuery } from "src/gql/schema"
import { useGenTable } from "./hooks/useGenTable"
import { FlexibleTableRow } from "src/shared/components/data-display/FlexibleTable/flexible-table-items.interface"
//import { useTurnoActual } from "src/pages/home/room-detail/hooks/turnos"
import { OrdenesPaths, OrdenesTabs, OrigenOrden } from "./Ordenes.types"
import Icon from "src/shared/icons"
import "./Ordenes.css"
import { useFormatDate } from "src/shared/hooks/useFormatDate"
// import useGetCurrentDate from "src/shared/hooks/useGetCurrentDate"
// import { getShortDateFormatted } from "src/utils/date"
import TablePaginatorWrapper from "src/shared/components/data-display/FlexibleTable/sections/TablePaginatorWrapper/TablePaginatorWrapper"
import InputDateModalDeselectable from "src/shared/components/forms/input-date/sections/InputDateModal/InputDateModalDeselectable"
import { debounce } from "src/utils/lodash"

interface OrdenesState {
    roomService: {
        list?: OrdenesQuery["ordenes"]
    }
    mostrador: {
        list?: OrdenesQuery["ordenes"]
    }
    restaurante: {
        list?: OrdenesQuery["ordenes"]
    }
    todas: {
        list?: OrdenesQuery["ordenes"]
    }
}

function Ordenes(): JSX.Element {
    const dispatch = useDispatch()

    const [rowsOrdenes, setRowsOrdenes] = useState<FlexibleTableRow[]>()
    const [ordenes, setOrdenes] = useState<OrdenesState>()
    const [type, setType] = useState<OrdenesPaths>("todas")
    const [tabs, setTabs] = useState<OrdenesTabs[]>([])
    const [load, setLoad] = useState<boolean>(true)
    const [openDateModal, setOpenDateModal] = useState(false)
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [search, setSearch] = useState<string>("")

    // Paginacion
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [variables, setVariables] = useState<any>({
        pagination_options: {
            take: 10,
            page: 1
        },
        estado: null,
        estado_pago: null,
        origen: null,
        turno_id: null,
        metodo_pago: null,
        orden: null,
        fecha_registro: null
    })

    const [showGradient, setShowGradient] = useState(false)
    const tableRef = useRef<FlexibleTableRef>(null)

    const { nowHotel: currentDate, getStartOfHotelDayISO, getEndOfHotelDayISO, formatCustomDate, getHotelDateParts } = useFormatDate()
    const todayHotel = getHotelDateParts(currentDate)
    const dateHotel = new Date(todayHotel.year, todayHotel.month, todayHotel.day)
    const navigate = useNavigate()

    const { restaurante: withRestaurante } = useModulos()
    const { genTable } = useGenTable()
    const { headers } = useHeader(ordenes?.todas?.list || [], type, withRestaurante)
    //const turnoActual = useTurnoActual()
    const { hotel_id } = useProfile()

    const { isDrawerOpen } = useSelector((state: RootState) => state.navigation)

    const [getOrdenes] = useOrdenesPaginadasLazyQuery()

    const toggleDrawerState = (value: boolean) => {
        dispatch(toggleDrawer(value))
    }

    const getData = async () => {
        try {
            const { data } = await getOrdenes({
                variables: {
                    hotel_id: hotel_id,
                    ...variables
                },
            })

            const dataOrdenes = data?.ordenes_paginadas?.ordenes || []
            const conteos_back = data?.ordenes_paginadas?.conteos

            setTotalPages(data?.ordenes_paginadas?.paginacion?.total_paginas || 0)
            if (dataOrdenes?.length > 0) {
                const ordenarDescendentePorFecha = (ordenesList: OrdenesQuery["ordenes"]) =>
                    [...ordenesList].sort(
                        (a, b) => new Date(b.fecha_registro).getTime() - new Date(a.fecha_registro).getTime()
                    )
                const filterOrdenes = withRestaurante
                    ? dataOrdenes
                    : dataOrdenes?.filter((orden) => orden.origen_orden !== OrigenOrden.Restaurante)

                const todas = ordenarDescendentePorFecha(filterOrdenes)

                // Ajusta el conteo según el tab seleccionado
                const total_registros = data?.ordenes_paginadas?.paginacion?.total_registros || 0
                const tabCount = (path: string) => {
                    if (path === type) return total_registros
                    const key = path === "todas" ? "total" : path
                    return (conteos_back?.[key as keyof typeof conteos_back] as number) || 0
                }

                setOrdenes({
                    roomService: { list: [] },
                    mostrador: { list: [] },
                    restaurante: { list: [] },
                    todas: { list: todas },
                })
                buildTable(todas)
                setTabs([
                    { label: "Todas las órdenes", path: "todas", number: tabCount("todas") },
                    { label: "Room service", path: "room_service", number: tabCount("room_service") },
                    { label: "Mostrador", path: "mostrador", number: tabCount("mostrador") },
                    ...(withRestaurante
                        ? [{ label: "Restaurante", path: "restaurante", number: tabCount("restaurante") }]
                        : []),
                ])
            } else {
                setTabs([
                    { label: "Todas las órdenes", path: "todas", number: 0 },
                    { label: "Room service", path: "room_service", number: 0 },
                    { label: "Mostrador", path: "mostrador", number: 0 },
                    ...(withRestaurante ? [{ label: "Restaurante", path: "restaurante", number: 0 }] : []),
                ])
                setRowsOrdenes([])
                setOrdenes({ roomService: {}, restaurante: {}, mostrador: {}, todas: {} })
            }
        } catch (e) {
            console.log(e)
        } finally {
            setLoad(false)
        }
    }

    const buildTable = (ordenes: OrdenesQuery["ordenes"] = []) => {
        setRowsOrdenes(genTable({ ordenes, filter: type }))
    }

    const handleFilterTable = (filters: TableFilter[]) => {
        if (filters.length === 0) {
            // No actualiza si los filtros ya están limpios
            if (!variables.estado && !variables.estado_pago && !variables.turno_id && !variables.metodo_pago) return
            setVariables({
                ...variables,
                estado: null,
                estado_pago: null,
                origen: type !== "todas" ? variables?.origen || null : null,
                turno_id: null,
                metodo_pago: null,
                pagination_options: { take: 10, page: 1 },
            })
            return
        }

        // Agrupa los filtros por columna
        const filtersByIdx = new Map<number, string[]>()
        filters.forEach(({ idx, filter }) => {
            const current = filtersByIdx.get(idx) || []
            filtersByIdx.set(idx, [...current, filter])
        })

        const getFilterValues = (values: string[]) => {
            const selectedValues = values.filter((v) => !v.includes("todos"))
            return selectedValues.length > 0 ? selectedValues : null
        }

        const newVars: any = {
            ...variables,
            // Reinicia los filtros
            estado: null,
            estado_pago: null,
            turno_id: null,
            metodo_pago: null,
            origen: type !== "todas" ? variables?.origen || null : null,
            pagination_options: { take: 10, page: 1 },
        }

        filtersByIdx.forEach((values, idx) => {
            const filterValues = getFilterValues(values)
            if (idx === 1) newVars.estado = filterValues
            else if (idx === 2 && type === "todas") newVars.origen = filterValues
            else if (idx === 3) newVars.turno_id = filterValues
            else if (idx === 8) newVars.metodo_pago = filterValues
            else if (idx === 9) newVars.estado_pago = filterValues
        })

        if (newVars.estado?.includes("en_servicio")) {
            const hasSelectedEnPreparacion = newVars.estado.includes(EstadosOrdenHistorial.EnPreparacion)
            const orderStates = newVars.estado.filter((estado: string) => estado !== "en_servicio")

            if (hasSelectedEnPreparacion) {
                newVars.estado = orderStates
            } else {
                newVars.estado = [...new Set([...orderStates, EstadosOrdenHistorial.EnPreparacion])]
                newVars.estado_pago = ["pagada"]
                newVars.origen = [OrigenOrden.Restaurante]
            }
        }

        setVariables(newVars)
    }

    const handleSearch = useCallback(
        debounce((value: string) => {
            setSelectedDate(null)
            setLoad(true)
            setVariables({
                pagination_options: {
                    take: 10,
                    page: 1
                },
                estado: null,
                estado_pago: null,
                origen: null,
                turno_id: null,
                metodo_pago: null,
                fecha_registro: null,
                orden: value || null
            })
        }, 500),
        [variables]
    )

    const handleDate = (v: Date | null) => {
        setLoad(true)
        setSelectedDate(v)
        setSearch("")

        const fecha_registro = v
            ? {
                fecha_inicial: getStartOfHotelDayISO(v),
                fecha_final: getEndOfHotelDayISO(v),
            }
            : null

        setVariables({
            pagination_options: {
                take: 10,
                page: 1
            },
            estado: null,
            estado_pago: null,
            origen: null,
            turno_id: null,
            metodo_pago: null,
            fecha_registro,
            orden: null
        })
    }

    /*
    const filterData = (search) => {
        //función para obtener los datos de la orden por filtro
        const arrayOrders =
            type === "todas"
                ? ordenes?.todas?.list
                : type === "room_service"
                ? ordenes?.roomService?.list
                : type === "restaurante"
                ? ordenes?.restaurante?.list
                : ordenes?.mostrador?.list
        if (search) {
            const filterData = arrayOrders?.filter((orden: any) =>
                `RS-${orden.orden}`.toLowerCase().includes(`${search}`.toLowerCase())
            )
            if (filterData) {
                buildTable(filterData)
            }
        } else {
            buildTable(arrayOrders || [])
        }
    }
    */

    useEffect(() => {
        getData()
    }, [withRestaurante, variables])

    useEscapeKey({
        onEscape: () => {
            navigate(-1)
        },
    })

    return (
        <Screen
            title={"Órdenes"}
            subtitle={selectedDate ? formatCustomDate(getStartOfHotelDayISO(selectedDate), "MMM, DD YYYY") : ""}
            className="ordenes__screen"
            contentClassName="ordenes"
            back={true}
            onBack={() => {
                navigate(-1)
                if (getSaveItems()) removeItems()
            }}
            headerRight={
                <div className="ordenes__search">
                    <InputText
                        icon={Icon}
                        iconProps={{ name: "searchLg", color: "var(--primary)" }}
                        className="ordenes__search__input"
                        type={"text"}
                        placeholder="Busca por número de órden"
                        value={search}
                        onChange={(e) => {
                            const value = e.target.value
                            setSearch(value)
                            handleSearch(value)
                        }}
                    />
                    <button
                        onClick={() => setOpenDateModal(true)}
                        className={`ordenes__icon-calendar ${selectedDate ? "ordenes__icon-calendar--active" : ""}`}
                    >
                        <Icon
                            name="calendar"
                            height={18}
                            width={18}
                            color={selectedDate ? "var(--primary)" : undefined}
                        />
                    </button>

                    <InputDateModalDeselectable
                        isOpen={openDateModal}
                        onClose={() => setOpenDateModal(false)}
                        isRange={false}
                        value={selectedDate ? [selectedDate] : []}
                        onChange={(date) => {
                            if (!date || (Array.isArray(date) && date.length === 0)) {
                                handleDate(null)
                                return
                            }
                            const selected = Array.isArray(date) ? date[0] : date
                            const isTodayOrPast = selected <= dateHotel
                            if (!isTodayOrPast) {
                                return
                            }

                            handleDate(selected)
                        }}
                        onReset={() => {
                            handleDate(null)
                        }}
                        onConfirm={() => setOpenDateModal(false)}
                        modalClosableOnClickOutside={true}
                        disabledAfterOrEqualDate={
                            new Date(dateHotel.getFullYear(), dateHotel.getMonth(), dateHotel.getDate() + 1)
                        }
                        maxDate={dateHotel}
                        allowDateDeselect={true}
                    />
                </div>
            }
        >
            <section className="ordenes__container">
                <div className="ordenes__container-filters">
                    <TabMenu
                        className="ordenes__container-tabs"
                        tabList={tabs}
                        value={type}
                        onChange={(value) => {
                            setType(value as OrdenesPaths)
                            tableRef.current?.setFiltersFromOuterState([])
                            setVariables({
                                ...variables,
                                origen: value !== "todas" ? value : null,
                                estado: null,
                                estado_pago: null,
                                turno_id: null,
                                metodo_pago: null,
                                pagination_options: { take: 10, page: 1 },
                            })
                        }}
                        showNumerOnNoItems={true}
                    />
                </div>
                {load ? (
                    <div className="ordenes__table">
                        <TableSkeleton headers={headers} />
                    </div>
                ) : (
                    <div className="ordenes__table">
                        <TablePaginatorWrapper
                            pages={totalPages > 1 ? totalPages : 0}
                            currentPage={currentPage}
                            onChange={(page)=>{
                                setCurrentPage(page)
                                setVariables({...variables, pagination_options: { take: 10, page }})
                            }}
                        >
                            <div
                                className={`ordenes__table-container${
                                    showGradient ? " ordenes__table-container--show-gradient" : ""
                                }`}
                            >
                                <FlexibleTable
                                    ref={tableRef}
                                    onSelectedFilters={handleFilterTable}
                                    onHasScrollChange={setShowGradient}
                                    tableItems={{
                                        ...{
                                            headers: headers,
                                            rows:
                                                rowsOrdenes?.map((row) => ({
                                                    value: row?.value?.map(({ value }, index, arr) => ({
                                                        value,
                                                        filterValue: value?.toString().toLowerCase() || "todos_turnos",
                                                        fromHeaderFilter: headers[index].value,
                                                    })),
                                                    goTo: row.goTo,
                                                })) || [],
                                        },
                                    }}
                                    goTo={(value) => {
                                        toggleDrawerState(true)
                                        dispatch(selectOrder(value))
                                    }}
                                    emptyState={{
                                        titile: "No tienes órdenes registradas",
                                        headerIcon: "dollarCircle",
                                    }}
                                />
                            </div>
                        </TablePaginatorWrapper>
                    </div>
                )}
            </section>
            <DrawerSection
                visible={isDrawerOpen}
                onClose={() => {
                    toggleDrawerState(false)
                    dispatch(selectOrder(""))
                }}
            />
        </Screen>
    )
}

export default Ordenes
